import {
  OWL_WINDOW_HEIGHT,
  OWL_WINDOW_WIDTH,
  type OwlPixelImage,
  type OwlWindowImage,
  type OwlWindowRect,
} from 'maplestory-owl-transcribe';
import {
  convertIndexedToRgb,
  decode,
  encode,
  hasPngSignature,
  type DecodedPng,
} from 'fast-png';
import { normalizeOpaqueImageToRgb8 } from 'fredrick-data-contract';

function pngChannels(decoded: DecodedPng): 3 | 4 {
  if (decoded.palette) {
    const lengths = new Set(decoded.palette.map((entry) => entry.length));
    if (lengths.size !== 1 || (!lengths.has(3) && !lengths.has(4))) {
      throw new TypeError('Indexed PNG palette must contain only RGB or RGBA entries.');
    }
    return decoded.palette[0]!.length as 3 | 4;
  }
  if (decoded.channels !== 3 && decoded.channels !== 4) {
    throw new TypeError('PNG must decode to RGB, RGBA, or an RGB/RGBA palette.');
  }
  return decoded.channels;
}

/** Decode PNG bytes without canvas color conversion or alpha premultiplication. */
export function decodePngPixelImage(bytes: Uint8Array): OwlPixelImage {
  if (!hasPngSignature(bytes)) {
    throw new TypeError('Source image must be a PNG file.');
  }

  const decoded = decode(bytes, { checkCrc: true });
  if (decoded.depth !== 8) {
    throw new TypeError('PNG must use 8-bit color channels.');
  }

  const channels = pngChannels(decoded);
  const data = decoded.palette
    ? convertIndexedToRgb(decoded)
    : decoded.data;
  if (!(data instanceof Uint8Array) && !(data instanceof Uint8ClampedArray)) {
    throw new TypeError('PNG must decode to unsigned 8-bit pixels.');
  }

  return {
    width: decoded.width,
    height: decoded.height,
    data,
    format: channels === 3 ? 'rgb8' : 'rgba8',
  };
}

export async function fileToPixelImage(file: Blob): Promise<OwlPixelImage> {
  return decodePngPixelImage(new Uint8Array(await file.arrayBuffer()));
}

/** Convert a supported decoded image to the exact RGB8 image used for hashing and OCR. */
export interface CanonicalRgbImage extends OwlPixelImage {
  readonly data: Uint8Array;
  readonly format: 'rgb8';
}

export function canonicalRgbImage(image: OwlPixelImage): CanonicalRgbImage {
  return {
    width: image.width,
    height: image.height,
    data: normalizeOpaqueImageToRgb8(image),
    format: 'rgb8',
    strideBytes: image.width * 3,
  };
}

/** Copy the canonical Owl window from a decoded source image. */
export function cropOwlWindowImage(image: OwlPixelImage, rect: OwlWindowRect): OwlWindowImage {
  if (rect.width !== OWL_WINDOW_WIDTH || rect.height !== OWL_WINDOW_HEIGHT) {
    throw new RangeError(`Owl window must be ${OWL_WINDOW_WIDTH}×${OWL_WINDOW_HEIGHT}.`);
  }
  if (
    !Number.isSafeInteger(rect.x)
    || !Number.isSafeInteger(rect.y)
    || rect.x < 0
    || rect.y < 0
    || rect.x + rect.width > image.width
    || rect.y + rect.height > image.height
  ) {
    throw new RangeError('Owl window is outside the source image.');
  }

  const channels = image.format === 'rgb8' ? 3 : 4;
  const sourceStride = image.strideBytes ?? image.width * channels;
  const targetStride = rect.width * channels;
  const data = new Uint8Array(targetStride * rect.height);
  for (let y = 0; y < rect.height; y += 1) {
    const sourceOffset = (rect.y + y) * sourceStride + rect.x * channels;
    data.set(image.data.subarray(sourceOffset, sourceOffset + targetStride), y * targetStride);
  }
  return {
    width: OWL_WINDOW_WIDTH,
    height: OWL_WINDOW_HEIGHT,
    data,
    format: image.format,
    strideBytes: targetStride,
  };
}

/** Encode canonical private evidence once as a lossless RGB PNG. */
export function encodeOwlWindowPng(image: OwlWindowImage): Blob {
  const rgb = normalizeOpaqueImageToRgb8(image);
  const bytes = encode({
    width: OWL_WINDOW_WIDTH,
    height: OWL_WINDOW_HEIGHT,
    data: rgb,
    depth: 8,
    channels: 3,
  });
  const blobBytes = Uint8Array.from(bytes);
  return new Blob([blobBytes.buffer], { type: 'image/png' });
}
